import { cn } from "@/lib/utils";
import { useState } from "react";

interface LilDragonsInnaeTabContentProps {
  hasTrainingVideo: (aliases: string[]) => boolean;
  handleWatchTrainingVideo: (aliases: string[]) => void;
}

export function LilDragonsInnaeTabContent({
  hasTrainingVideo,
  handleWatchTrainingVideo,
}: LilDragonsInnaeTabContentProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <div>
      {/* Mobile only: title → video → 1–5 nav → selected stance */}
      <div className="flex flex-col gap-4 md:hidden">
        <div className="flex flex-col gap-2">
          <h3 className="text-lg font-semibold text-taekwondo-red">Innae</h3>
          {hasTrainingVideo(["innae"]) && (
            <button
              type="button"
              className="inline-flex w-fit items-center text-sm font-medium text-taekwondo-blue hover:underline"
              onClick={() => handleWatchTrainingVideo(["innae"])}
            >
              Watch Demonstration Video
            </button>
          )}
        </div>
        <div
          className="flex w-full justify-between gap-1"
          role="tablist"
          aria-label="Innae sequence"
        >
          {[1, 2, 3, 4, 5].map((num, index) => {
            const isSelected = activeIndex === index;
            return (
              <button
                key={num}
                type="button"
                role="tab"
                aria-selected={isSelected}
                aria-label={`Innae ${num}`}
                onClick={() => setActiveIndex(index)}
                className={cn(
                  "flex min-w-0 flex-1 flex-col items-center justify-center rounded-xl border-2 bg-white px-1 py-2 text-lg font-semibold tabular-nums transition-colors",
                  isSelected
                    ? "border-taekwondo-blue shadow-md shadow-taekwondo-blue/15 ring-2 ring-taekwondo-blue/25 text-taekwondo-red"
                    : "border-gray-200 text-gray-800 hover:border-taekwondo-blue/40",
                )}
              >
                {num}
              </button>
            );
          })}
        </div>
        <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm flex flex-col md:flex-row items-start gap-4" role="tabpanel">
          {activeIndex === 0 && (
            <>
              <img
                src="/images/dragons/dragon_punch.png"
                alt="Lil' Dragon Innae 1"
                className="h-24 w-auto flex-shrink-0"
                loading="lazy"
                decoding="async"
              />
              <div className="text-left text-sm text-gray-700">
                <h4 className="text-md font-semibold mb-1">Innae 1 (인내 1)</h4>
                <ol className="list-decimal list-inside space-y-1 marker:text-taekwondo-red">
                  <li>
                    Cover across body with right hand stepping right leg into Horse Stance left middle punch{" "}
                    <span className="font-semibold">KIHAP</span>
                  </li>
                  <li>
                    Right middle punch <span className="font-semibold">KIHAP</span>
                  </li>
                  <li>
                    2 x high punches (L/R) on 2nd <span className="font-semibold">KIHAP</span>
                  </li>
                  <li>
                    Triple punch (2 mid (L/R), 1 L high) <span className="font-semibold">KIHAP</span>
                  </li>
                  <li>
                    Repeat triple punches 2 mid R/L, 1 R high{" "}
                    <span className="font-semibold">KIHAP</span>
                  </li>
                </ol>
              </div>
            </>
          )}
          {activeIndex === 1 && (
            <>
              <img
                src="/images/dragons/dragon_punch.png"
                alt="Lil' Dragon Innae 2"
                className="h-24 w-auto flex-shrink-0"
                loading="lazy"
                decoding="async"
              />
              <div className="text-left text-sm text-gray-700">
                <h4 className="text-md font-semibold mb-1">Innae 2 (인내 2)</h4>
                <ol className="list-decimal list-inside space-y-1 marker:text-taekwondo-red">
                  <li>Step right leg back Back Stance down block</li>
                  <li>
                    Adjust front leg to FS mid section punch{" "}
                    <span className="font-semibold">KIHAP</span>
                  </li>
                  <li>Repeat other side</li>
                </ol>
              </div>
            </>
          )}
          {activeIndex === 2 && (
            <>
              <img
                src="/images/dragons/dragon_punch.png"
                alt="Lil' Dragon Innae 3"
                className="h-24 w-auto flex-shrink-0"
                loading="lazy"
                decoding="async"
              />
              <div className="text-left text-sm text-gray-700">
                <h4 className="text-md font-semibold mb-1">Innae 3 (인내 3)</h4>
                <ol className="list-decimal list-inside space-y-1 marker:text-taekwondo-red">
                  <li>Step right leg back Back Stance rising block</li>
                  <li>
                    Adjust front leg to Front Stance high section punch{" "}
                    <span className="font-semibold">KIHAP</span>
                  </li>
                  <li>Repeat other side</li>
                </ol>
              </div>
            </>
          )}
          {activeIndex === 3 && (
            <>
              <img
                src="/images/dragons/dragon_punch.png"
                alt="Lil' Dragon Innae 4"
                className="h-24 w-auto flex-shrink-0"
                loading="lazy"
                decoding="async"
              />
              <div className="text-left text-sm text-gray-700">
                <h4 className="text-md font-semibold mb-1">Innae 4 (인내 4)</h4>
                <ol className="list-decimal list-inside space-y-1 marker:text-taekwondo-red">
                  <li>
                    Step right leg back Cat Stance inner forearm outer block with front arm L
                  </li>
                  <li>
                    Keep stance mid section punch <span className="font-semibold">KIHAP</span>
                  </li>
                  <li>Repeat other side</li>
                </ol>
              </div>
            </>
          )}
          {activeIndex === 4 && (
            <>
              <img
                src="/images/dragons/dragon_punch.png"
                alt="Lil' Dragon Innae 5"
                className="h-24 w-auto flex-shrink-0"
                loading="lazy"
                decoding="async"
              />
              <div className="text-left text-sm text-gray-700">
                <h4 className="text-md font-semibold mb-1">Innae 5 (인내 5)</h4>
                <ol className="list-decimal list-inside space-y-1 marker:text-taekwondo-red">
                  <li>Step right leg back Back Stance single knifehand block (L)</li>
                  <li>
                    Adjust front leg to Front Stance reverse knifehand strike (R){" "}
                    <span className="font-semibold">KIHAP</span>
                  </li>
                  <li>Repeat other side</li>
                </ol>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Desktop: direct copy of original page markup */}
      <div className="mt-10 hidden md:block">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-4">
          <h3 className="text-lg font-semibold text-taekwondo-red">Innae</h3>
          {hasTrainingVideo(["innae"]) && (
            <button
              type="button"
              className="inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
              onClick={() => handleWatchTrainingVideo(["innae"])}
            >
              Watch Demonstration Video
            </button>
          )}
        </div>
        <div className="space-y-4">
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm flex flex-col md:flex-row items-start gap-4">
            <img
              src="/images/dragons/dragon_punch.png"
              alt="Lil' Dragon Innae 1"
              className="h-24 w-auto flex-shrink-0"
            />
            <div className="text-left text-sm text-gray-700">
              <h4 className="text-md font-semibold mb-1">Innae 1 (인내 1)</h4>
              <ol className="list-decimal list-inside space-y-1 marker:text-taekwondo-red">
                <li>
                  Cover across body with right hand stepping right leg into Horse Stance left middle punch{" "}
                  <span className="font-semibold">KIHAP</span>
                </li>
                <li>Right middle punch <span className="font-semibold">KIHAP</span></li>
                <li>
                  2 x high punches (L/R) on 2nd <span className="font-semibold">KIHAP</span>
                </li>
                <li>
                  Triple punch (2 mid (L/R), 1 L high) <span className="font-semibold">KIHAP</span>
                </li>
                <li>
                  Repeat triple punches 2 mid R/L, 1 R high{" "}
                  <span className="font-semibold">KIHAP</span>
                </li>
              </ol>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm flex flex-col md:flex-row items-start gap-4">
            <img
              src="/images/dragons/dragon_punch.png"
              alt="Lil' Dragon Innae 2"
              className="h-24 w-auto flex-shrink-0"
            />
            <div className="text-left text-sm text-gray-700">
              <h4 className="text-md font-semibold mb-1">Innae 2 (인내 2)</h4>
              <ol className="list-decimal list-inside space-y-1 marker:text-taekwondo-red">
                <li>Step right leg back Back Stance down block</li>
                <li>
                  Adjust front leg to FS mid section punch{" "}
                  <span className="font-semibold">KIHAP</span>
                </li>
                <li>Repeat other side</li>
              </ol>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm flex flex-col md:flex-row items-start gap-4">
            <img
              src="/images/dragons/dragon_punch.png"
              alt="Lil' Dragon Innae 3"
              className="h-24 w-auto flex-shrink-0"
            />
            <div className="text-left text-sm text-gray-700">
              <h4 className="text-md font-semibold mb-1">Innae 3 (인내 3)</h4>
              <ol className="list-decimal list-inside space-y-1 marker:text-taekwondo-red">
                <li>Step right leg back Back Stance rising block</li>
                <li>
                  Adjust front leg to Front Stance high section punch{" "}
                  <span className="font-semibold">KIHAP</span>
                </li>
                <li>Repeat other side</li>
              </ol>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm flex flex-col md:flex-row items-start gap-4">
            <img
              src="/images/dragons/dragon_punch.png"
              alt="Lil' Dragon Innae 4"
              className="h-24 w-auto flex-shrink-0"
            />
            <div className="text-left text-sm text-gray-700">
              <h4 className="text-md font-semibold mb-1">Innae 4 (인내 4)</h4>
              <ol className="list-decimal list-inside space-y-1 marker:text-taekwondo-red">
                <li>
                  Step right leg back Cat Stance inner forearm outer block with front arm L
                </li>
                <li>
                  Keep stance mid section punch <span className="font-semibold">KIHAP</span>
                </li>
                <li>Repeat other side</li>
              </ol>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm flex flex-col md:flex-row items-start gap-4">
            <img
              src="/images/dragons/dragon_punch.png"
              alt="Lil' Dragon Innae 5"
              className="h-24 w-auto flex-shrink-0"
            />
            <div className="text-left text-sm text-gray-700">
              <h4 className="text-md font-semibold mb-1">Innae 5 (인내 5)</h4>
              <ol className="list-decimal list-inside space-y-1 marker:text-taekwondo-red">
                <li>Step right leg back Back Stance single knifehand block (L)</li>
                <li>
                  Adjust front leg to Front Stance reverse knifehand strike (R){" "}
                  <span className="font-semibold">KIHAP</span>
                </li>
                <li>Repeat other side</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
